import PropTypes from "prop-types";
import { Calendar, Package, Repeat2 } from "lucide-react";
import { Card, CardHeader, MetadataField, Divider, HighlightedInfo, Badge } from "../ui";

const SubscriptionInfo = ({ metadata }) => {
  const display = metadata?.display || {};
  const hasInfo =
    display.plan_name ||
    display.start_on ||
    display.repeats ||
    display.trial_days > 0;

  if (!hasInfo) return null;

  const trialItems = [
    display.trial_days > 0 && { label: "Duration", value: `${display.trial_days} days` },
    display.trial_ends_on && {
      label: "Ends On",
      value: new Date(display.trial_ends_on).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    },
  ].filter(Boolean);

  return (
    <Card className="mb-4">
      <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2 sm:pb-3">
        <h3 className="text-sm sm:text-base font-bold" style={{ color: "#3B3B3B" }}>
          Subscription Details
        </h3>
      </div>
      <div className="px-4 sm:px-6 pb-4 sm:pb-5">
        <div className="space-y-1">
          {display.plan_name && (
            <div className="py-2 sm:py-3">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: "#515164" }} />
                <span className="text-xs sm:text-sm font-medium" style={{ color: "#3B3B3B" }}>
                  Plan
                </span>
              </div>
              <div className="pl-5 sm:pl-6">
                <span className="text-xs sm:text-sm font-semibold" style={{ color: "#01793B" }}>
                  {display.plan_name}
                </span>
              </div>
            </div>
          )}

          {display.plan_description && (
            <MetadataField
              label="Description"
              value={display.plan_description}
              isMultiline
            />
          )}

          {display.start_on && (
            <MetadataField
              icon={Calendar}
              label="Starts On"
              value={new Date(display.start_on).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            />
          )}

          {display.repeats && (
            <MetadataField
              icon={Repeat2}
              label="Repeats"
              value={
                display.repeats.charAt(0).toUpperCase() +
                display.repeats.slice(1)
              }
            />
          )}

          {display.trial_days > 0 && (
            <>
              <Divider />
              <HighlightedInfo
                icon={Calendar}
                title="Trial Period"
                items={trialItems}
              />
            </>
          )}

          {display.duration_months && display.trial_days <= 0 && (
            <MetadataField
              icon={Calendar}
              label="Duration"
              value={`${display.duration_months} months`}
            />
          )}

          {display.is_renewable !== undefined && (
            <div className="flex justify-between items-center py-2 sm:py-3 gap-2">
              <div className="flex items-center gap-2">
                <Repeat2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" style={{ color: "#515164" }} />
                <span className="text-xs sm:text-sm font-medium" style={{ color: "#3B3B3B" }}>
                  Auto-Renewal
                </span>
              </div>
              <Badge variant={display.is_renewable ? "success" : "default"}>
                {display.is_renewable ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

SubscriptionInfo.propTypes = {
  metadata: PropTypes.shape({
    display: PropTypes.shape({
      plan_name: PropTypes.string,
      plan_description: PropTypes.string,
      start_on: PropTypes.string,
      repeats: PropTypes.string,
      trial_days: PropTypes.number,
      trial_ends_on: PropTypes.string,
      duration_months: PropTypes.number,
      is_renewable: PropTypes.bool,
    }),
  }),
};

export default SubscriptionInfo;
